import { SubjectReference, WorkspaceSubjectType } from '@/types';

interface CompanySearchResultClearbit {
    category?: {
        industry?: string;
        sector?: string;
    };
    crunchbase?: { handle?: string };
    domain?: string;
    facebook?: { handle?: string };
    foundedYear?: number | string;
    logo?: string;
    linkedin?: { handle?: string };
    tags?: string[];
    twitter?: { handle?: string };
    type?: string;
}

interface CompanySearchResultEnrichment {
    address?: string;
    address2?: string;
    assetType?: string;
    ceo?: string;
    cik?: string;
    city?: string;
    clearbit?: CompanySearchResultClearbit;
    companyName?: string;
    country?: string;
    description?: string;
    exchange?: string;
    figi?: string;
    fullTimeEmployees?: number | string;
    industry?: string;
    ipoDate?: Date | string;
    isAdr?: boolean;
    isEtf?: boolean;
    isFund?: boolean;
    isIndex?: boolean;
    issueType?: string;
    lei?: string;
    logo?: string;
    sector?: string;
    securityType?: string;
    symbol?: string;
    type?: string;
    website?: string;
}

export interface CompanySearchResult {
    [key: string]: unknown;
    assetType?: string;
    companyName?: string;
    enrichment?: CompanySearchResultEnrichment;
    entityId?: string;
    exchange?: string;
    id?: string;
    isEtf?: boolean;
    isFund?: boolean;
    isIndex?: boolean;
    logo?: string;
    name?: string;
    resultType?: string;
    sector?: string;
    securityType?: string;
    symbol?: string;
    ticker?: string;
    type?: string;
    website?: string;
}

export interface CompanySearchResultGroup {
    alternatives: CompanySearchResult[];
    id: string;
    label: string;
    primary: CompanySearchResult;
}

export interface CompanyListingSummary {
    equities: number;
    funds: number;
    indices: number;
    total: number;
}

const US_EXCHANGE_PRIORITY = [
    'NASDAQ',
    'New York Stock Exchange',
    'NYSE',
    'NYSE Arca',
    'NYSE American',
    'AMEX',
    'Cboe BZX',
];

const COMPANY_SUFFIX_PATTERN =
    /\b(incorporated|inc|corporation|corp|company|co|limited|ltd|plc|sa|ag|nv|holdings|holding|class\s+[a-z])\b/gi;

const SECURITY_DESCRIPTOR_PATTERN =
    /\b(adr|ads|american depositary|depositary|ordinary shares?|common shares?|common stock|preferred shares?|preference shares?|sponsored|unsponsored|each representing|class\s+[a-z]|series\s+[a-z0-9]+)\b/gi;

const NON_DISTINCTIVE_GROUP_TOKENS = new Set([
    'the',
    'and',
    'of',
    'bank',
    'banc',
    'banco',
    'capital',
    'financial',
    'group',
    'global',
    'international',
    'investment',
    'investments',
    'partners',
    'holdings',
    'holding',
    'company',
    'companies',
    'corporation',
    'corp',
    'inc',
    'ltd',
    'limited',
    'plc',
]);

export const getCompanySearchResultId = (result: CompanySearchResult) =>
    result?.entityId ?? result?.symbol ?? result?.id ?? result?.name ?? result?.companyName;

export const getCompanySearchResultName = (result: CompanySearchResult) =>
    result?.enrichment?.companyName ?? result?.companyName ?? result?.name ?? result?.symbol ?? 'Unknown Company';

export const getCompanySearchResultExchange = (result: CompanySearchResult) =>
    result?.enrichment?.exchange ?? result?.exchange ?? 'Unknown Exchange';

export const companySearchResultToSubjectReference = (result: CompanySearchResult): SubjectReference | null => {
    const subjectId = result.entityId ?? result.symbol ?? result.id;

    if (!subjectId) {
        return null;
    }

    const exchange = result.enrichment?.exchange ?? result.exchange;
    const symbol = result.enrichment?.symbol ?? result.symbol ?? result.ticker;
    const website = result.enrichment?.website ?? result.website;

    return {
        metadata: {
            ...(exchange ? { exchange } : {}),
            ...(symbol ? { symbol } : {}),
            ...(website ? { website } : {}),
        },
        subjectId: String(subjectId),
        subjectLabel: getCompanySearchResultName(result),
        subjectType:
            result.resultType === 'importedEntity'
                ? WorkspaceSubjectType.IMPORTED_ENTITY
                : WorkspaceSubjectType.SEARCH_COMPANY,
    };
};

const getCompanySearchResultListingKind = (result: CompanySearchResult): keyof Omit<CompanyListingSummary, 'total'> => {
    const enrichment = result?.enrichment ?? {};
    const type = String(
        enrichment?.assetType ??
            enrichment?.securityType ??
            enrichment?.type ??
            result?.assetType ??
            result?.securityType ??
            result?.type ??
            '',
    ).toLowerCase();

    if (enrichment?.isIndex === true || result?.isIndex === true || type.includes('index')) {
        return 'indices';
    }

    if (
        enrichment?.isEtf === true ||
        enrichment?.isFund === true ||
        result?.isEtf === true ||
        result?.isFund === true ||
        type.includes('etf') ||
        type.includes('fund')
    ) {
        return 'funds';
    }

    return 'equities';
};

export const isCompanySearchResultFundLike = (result: CompanySearchResult) =>
    getCompanySearchResultListingKind(result) === 'funds';

/** Resolves one canonical Search detail destination across every result payload shape. */
export const getCompanySearchResultDetailRoute = (result: CompanySearchResult, tab?: string) => {
    if (result.resultType === 'nylasCompany') {
        return undefined;
    }

    if (result.resultType === 'importedEntity') {
        return result.entityId ? `/search/entities/${encodeURIComponent(result.entityId)}` : undefined;
    }

    const symbol = result.enrichment?.symbol ?? result.symbol ?? result.ticker;

    if (!symbol) {
        return undefined;
    }

    const collection = isCompanySearchResultFundLike(result) ? 'funds' : 'companies';
    const normalizedTab = tab?.replace(/^\/+|\/+$/g, '');

    return `/search/${collection}/${encodeURIComponent(symbol)}${normalizedTab ? `/${normalizedTab}` : ''}`;
};

export const summarizeCompanyListings = (results: CompanySearchResult[]): CompanyListingSummary =>
    results.reduce<CompanyListingSummary>(
        (summary, result) => {
            const listingKind = getCompanySearchResultListingKind(result);

            return {
                ...summary,
                [listingKind]: summary[listingKind] + 1,
                total: summary.total + 1,
            };
        },
        { equities: 0, funds: 0, indices: 0, total: 0 },
    );

const getCompanySearchResultSector = (result: CompanySearchResult) =>
    result?.enrichment?.sector ?? result?.sector ?? result?.enrichment?.clearbit?.category?.sector ?? '';

const getCompanySearchResultLogo = (result: CompanySearchResult) =>
    result?.enrichment?.clearbit?.logo ?? result?.enrichment?.logo ?? result?.logo ?? '';

const getCompanySearchResultWebsiteHost = (result: CompanySearchResult) => {
    const website = result?.enrichment?.website ?? result?.website ?? result?.enrichment?.clearbit?.domain ?? '';

    if (!website) {
        return '';
    }

    try {
        const url = new URL(String(website).startsWith('http') ? website : `https://${website}`);

        return url.hostname.replace(/^www\./, '').toLowerCase();
    } catch {
        return String(website)
            .replace(/^https?:\/\//, '')
            .replace(/^www\./, '')
            .split('/')[0]
            .toLowerCase();
    }
};

const normalizeCompanyName = (name: string) =>
    name
        .replace(/&/g, ' and ')
        .replace(SECURITY_DESCRIPTOR_PATTERN, '')
        .replace(COMPANY_SUFFIX_PATTERN, '')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

const getCompanySearchResultSymbolRoot = (result: CompanySearchResult) => {
    const symbol = String(result?.symbol ?? result?.ticker ?? '').trim();

    return symbol.split(/[.: -]/)[0]?.toLowerCase() ?? '';
};

const normalizeCompanyGroupName = (result: CompanySearchResult) =>
    normalizeCompanyName(getCompanySearchResultName(result));

const getCompanyNameTokens = (result: CompanySearchResult) =>
    normalizeCompanyGroupName(result)
        .split(' ')
        .filter((token) => token.length > 1 && !NON_DISTINCTIVE_GROUP_TOKENS.has(token));

const getCompanyFamilyKey = (result: CompanySearchResult) => {
    const [firstToken] = getCompanyNameTokens(result);

    return firstToken && firstToken.length >= 4 ? firstToken : '';
};

const getTokenOverlapRatio = (first: string[], second: string[]) => {
    if (first.length === 0 || second.length === 0) {
        return 0;
    }

    const firstTokens = new Set(first);
    const secondTokens = new Set(second);
    const overlap = [...firstTokens].filter((token) => secondTokens.has(token)).length;

    return overlap / Math.max(firstTokens.size, secondTokens.size);
};

const areCompanySearchResultsRelated = (
    first: CompanySearchResult,
    second: CompanySearchResult,
    familyKeyCounts: Map<string, number>,
) => {
    const firstName = normalizeCompanyGroupName(first);
    const secondName = normalizeCompanyGroupName(second);

    if (!firstName || !secondName) {
        return false;
    }

    if (firstName === secondName) {
        return true;
    }

    const firstSymbolRoot = getCompanySearchResultSymbolRoot(first);
    const secondSymbolRoot = getCompanySearchResultSymbolRoot(second);

    if (firstSymbolRoot && firstSymbolRoot === secondSymbolRoot) {
        return true;
    }

    const firstTokens = getCompanyNameTokens(first);
    const secondTokens = getCompanyNameTokens(second);
    const smallerTokenCount = Math.min(firstTokens.length, secondTokens.length);

    if (smallerTokenCount < 2) {
        return false;
    }

    const firstIncludesSecond = firstName.includes(secondName);
    const secondIncludesFirst = secondName.includes(firstName);

    if ((firstIncludesSecond || secondIncludesFirst) && getTokenOverlapRatio(firstTokens, secondTokens) >= 0.75) {
        return true;
    }

    if (getTokenOverlapRatio(firstTokens, secondTokens) >= 0.85) {
        return true;
    }

    const firstFamilyKey = getCompanyFamilyKey(first);
    const secondFamilyKey = getCompanyFamilyKey(second);

    if (!firstFamilyKey || firstFamilyKey !== secondFamilyKey || (familyKeyCounts.get(firstFamilyKey) ?? 0) < 3) {
        return false;
    }

    const firstWebsiteHost = getCompanySearchResultWebsiteHost(first);
    const secondWebsiteHost = getCompanySearchResultWebsiteHost(second);

    if (firstWebsiteHost && firstWebsiteHost === secondWebsiteHost) {
        return true;
    }

    const firstLogo = getCompanySearchResultLogo(first);
    const secondLogo = getCompanySearchResultLogo(second);

    if (firstLogo && firstLogo === secondLogo) {
        return true;
    }

    const firstSector = getCompanySearchResultSector(first);
    const secondSector = getCompanySearchResultSector(second);

    return Boolean(firstSector) && firstSector === secondSector;
};

const isUnitedStatesCompanySearchResult = (result: CompanySearchResult) =>
    result?.resultType === 'importedEntity' ||
    result?.enrichment?.country === 'US' ||
    US_EXCHANGE_PRIORITY.includes(getCompanySearchResultExchange(result));

const exchangePriority = (result: CompanySearchResult) => {
    const exchange = getCompanySearchResultExchange(result);
    const index = US_EXCHANGE_PRIORITY.findIndex((value) => exchange?.toLowerCase().includes(value.toLowerCase()));

    return index === -1 ? US_EXCHANGE_PRIORITY.length : index;
};

const compareCompanyResults = (first: CompanySearchResult, second: CompanySearchResult) => {
    const firstIsUs = isUnitedStatesCompanySearchResult(first);
    const secondIsUs = isUnitedStatesCompanySearchResult(second);

    if (firstIsUs !== secondIsUs) {
        return firstIsUs ? -1 : 1;
    }

    const priorityDelta = exchangePriority(first) - exchangePriority(second);

    if (priorityDelta !== 0) {
        return priorityDelta;
    }

    return String(getCompanySearchResultId(first)).localeCompare(String(getCompanySearchResultId(second)));
};

export const sortCompanySearchResults = (results: CompanySearchResult[] = []) => {
    const grouped = groupCompanySearchResults(results);

    return grouped.flatMap((group) => [group.primary, ...group.alternatives]);
};

export const groupCompanySearchResults = (results: CompanySearchResult[] = []): CompanySearchResultGroup[] => {
    const familyKeyCounts = results.reduce<Map<string, number>>((accumulator, result) => {
        const familyKey = getCompanyFamilyKey(result);

        if (familyKey) {
            accumulator.set(familyKey, (accumulator.get(familyKey) ?? 0) + 1);
        }

        return accumulator;
    }, new Map());

    const groups = results.reduce<CompanySearchResult[][]>((accumulator, result) => {
        const existingGroup = accumulator.find((groupResults) =>
            groupResults.some((groupResult) => areCompanySearchResultsRelated(groupResult, result, familyKeyCounts)),
        );

        if (existingGroup) {
            existingGroup.push(result);
        } else {
            accumulator.push([result]);
        }

        return accumulator;
    }, []);

    return groups.map((groupResults) => {
        const sorted = [...groupResults].sort(compareCompanyResults);
        const primary = sorted[0];

        return {
            alternatives: sorted.slice(1),
            id: normalizeCompanyGroupName(primary) || String(getCompanySearchResultId(primary)),
            label: getCompanySearchResultName(primary),
            primary,
        };
    });
};
